import { cn } from './cn.js';
import { Link } from 'react-router';

export type Tone = 'neutral' | 'muted' | 'scan' | 'signal' | 'good' | 'alarm';

const TONE_PILL: Record<Tone, string> = {
  neutral: 'border-slate-300 bg-slate-100 text-slate-900 font-semibold',
  muted: 'border-slate-300/80 bg-slate-100/80 text-slate-700 font-medium',
  scan: 'border-sky-300 bg-sky-50 text-sky-950 font-semibold',
  signal: 'border-amber-300 bg-amber-50 text-amber-950 font-semibold',
  good: 'border-emerald-300 bg-emerald-50 text-emerald-950 font-semibold',
  alarm: 'border-rose-300 bg-rose-50 text-rose-950 font-semibold',
};

const TONE_TEXT: Record<Tone, string> = {
  neutral: 'text-slate-900',
  muted: 'text-slate-700',
  scan: 'text-sky-800',
  signal: 'text-amber-800',
  good: 'text-emerald-800',
  alarm: 'text-rose-800',
};

const TONE_FILL: Record<Tone, string> = {
  neutral: 'bg-slate-600',
  muted: 'bg-slate-500',
  scan: 'bg-sky-600',
  signal: 'bg-amber-600',
  good: 'bg-emerald-600',
  alarm: 'bg-rose-600',
};

const TONE_STROKE: Record<Tone, string> = {
  neutral: 'stroke-slate-600',
  muted: 'stroke-slate-500',
  scan: 'stroke-sky-600',
  signal: 'stroke-amber-600',
  good: 'stroke-emerald-600',
  alarm: 'stroke-rose-600',
};

export interface BadgeProps {
  readonly children: React.ReactNode;
  readonly tone?: Tone;
  readonly className?: string;
  readonly title?: string;
}

/** Modern status pill with subtle border and clear tone contrast. */
export function Badge({
  children,
  tone = 'neutral',
  className,
  title,
}: BadgeProps): React.ReactElement {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold tracking-wide whitespace-nowrap shrink-0',
        TONE_PILL[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Lamp({
  tone = 'good',
  pulse = false,
  label,
}: {
  readonly tone?: Tone;
  readonly pulse?: boolean;
  readonly label?: string;
}): React.ReactElement {
  return (
    <span className="inline-flex items-center gap-2">
      <span
        aria-hidden="true"
        className={cn('h-2 w-2 rounded-full', TONE_FILL[tone], pulse && 'animate-pulse-soft')}
      />
      {label !== undefined && (
        <span className="text-[11px] font-semibold text-ink-soft">{label}</span>
      )}
    </span>
  );
}

export interface MeterProps {
  readonly value: number;
  readonly tone?: Tone;
  readonly className?: string;
}

/** Progress ribbon for a 0..1 ratio. */
export function Meter({ value, tone = 'good', className }: MeterProps): React.ReactElement {
  const clamped = Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
  return (
    <div
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(clamped * 100)}
      className={cn('h-1.5 w-full overflow-hidden rounded-full bg-cream', className)}
    >
      <div
        className={cn('h-full rounded-full transition-[width] duration-300', TONE_FILL[tone])}
        style={{ width: `${clamped * 100}%` }}
      />
    </div>
  );
}

export interface StatProps {
  readonly label: string;
  readonly value: string;
  readonly unit?: string;
  readonly hint?: string;
  readonly tone?: Tone;
  readonly fill?: number;
  readonly index?: string;
  readonly to?: string;
  readonly onClick?: () => void;
  readonly className?: string;
}

/** Modern KPI card with clean sans-serif typography, subtle progress meter, and optional Link/onClick. */
export function Stat({
  label,
  value,
  unit,
  hint,
  tone = 'neutral',
  fill,
  index,
  to,
  onClick,
  className,
}: StatProps): React.ReactElement {
  const isClickable = Boolean(to || onClick);
  const cardContent = (
    <>
      <div className="flex items-center justify-between gap-2">
        <p className={cn('eyebrow text-ink-faint', isClickable && 'group-hover:text-ink transition-colors')}>
          {label}
        </p>
        <div className="flex items-center gap-1.5">
          {index !== undefined && (
            <span
              aria-hidden="true"
              className="grid h-6 w-6 place-items-center rounded-md bg-cream text-[10px] font-bold text-ink-faint font-mono"
            >
              {index}
            </span>
          )}
          {to !== undefined && (
            <span
              aria-hidden="true"
              className="text-xs text-ink-ghost group-hover:text-clay-600 transition-all duration-150 group-hover:translate-x-0.5"
            >
              →
            </span>
          )}
        </div>
      </div>
      <p className={cn('tabular mt-2 text-3xl font-bold tracking-tight leading-none', TONE_TEXT[tone])}>
        {value}
        {unit !== undefined && (
          <span className="ml-1 text-sm font-normal text-ink-faint">{unit}</span>
        )}
      </p>
      {hint !== undefined && <p className="mt-2 text-xs text-ink-soft leading-normal">{hint}</p>}
      {fill !== undefined && (
        <Meter value={fill} tone={tone === 'neutral' ? 'good' : tone} className="mt-3.5" />
      )}
    </>
  );

  const sharedClasses = cn(
    'rounded-card border border-border bg-card p-5 shadow-card transition-all duration-150 block text-left',
    isClickable
      ? 'cursor-pointer group hover:border-clay-500/50 hover:shadow-md hover:-translate-y-0.5'
      : 'hover:border-border-strong',
    className,
  );

  if (to !== undefined) {
    return (
      <Link to={to} className={sharedClasses} onClick={onClick}>
        {cardContent}
      </Link>
    );
  }

  return (
    <div onClick={onClick} className={sharedClasses}>
      {cardContent}
    </div>
  );
}

const GAUGE_R = 52;
const GAUGE_C = 2 * Math.PI * GAUGE_R;
const GAUGE_SWEEP = GAUGE_C * 0.75;

export interface GaugeProps {
  readonly value: number;
  readonly label: string;
  readonly caption?: string;
  readonly tone?: Tone;
}

/** Modern 270° ring gauge for coverage KPIs. */
export function Gauge({ value, label, caption, tone = 'good' }: GaugeProps): React.ReactElement {
  const clamped = Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
  return (
    <div className="flex flex-col items-center">
      <svg
        viewBox="0 0 128 128"
        className="h-32 w-32"
        role="img"
        aria-label={`${label} : ${Math.round(clamped * 100)} %`}
      >
        <circle
          cx="64"
          cy="64"
          r={GAUGE_R}
          fill="none"
          strokeWidth="8"
          strokeLinecap="round"
          className="stroke-cream"
          strokeDasharray={`${GAUGE_SWEEP} ${GAUGE_C}`}
          transform="rotate(135 64 64)"
        />
        <circle
          cx="64"
          cy="64"
          r={GAUGE_R}
          fill="none"
          strokeWidth="8"
          strokeLinecap="round"
          className={cn('transition-[stroke-dasharray] duration-500', TONE_STROKE[tone])}
          strokeDasharray={`${GAUGE_SWEEP * clamped} ${GAUGE_C}`}
          transform="rotate(135 64 64)"
        />
        <text
          x="64"
          y="66"
          textAnchor="middle"
          className={cn('font-bold fill-current text-[24px]', TONE_TEXT[tone])}
        >
          {Math.round(clamped * 100)}
        </text>
        <text x="64" y="82" textAnchor="middle" className="fill-ink-faint text-[10px] font-semibold">
          %
        </text>
      </svg>
      <p className="eyebrow mt-1 text-center text-ink-soft">{label}</p>
      {caption !== undefined && (
        <p className="mt-0.5 max-w-40 text-center text-xs text-ink-faint">{caption}</p>
      )}
    </div>
  );
}

const BAR_COLOR: Record<Tone, string> = {
  neutral: 'bg-ink-soft',
  muted: 'bg-ink-ghost',
  scan: 'bg-sky-600',
  signal: 'bg-clay-600',
  good: 'bg-herb-600',
  alarm: 'bg-rust-600',
};

export interface BarcodeProps {
  readonly value: string | null | undefined;
  readonly tone?: Tone;
  readonly height?: number;
  readonly className?: string;
}

/** Modern Barcode fingerprint glyph. */
export function Barcode({
  value,
  tone = 'muted',
  height = 18,
  className,
}: BarcodeProps): React.ReactElement | null {
  const digits = (value ?? '').replace(/\D/g, '');
  if (digits.length < 4) return null;

  const bars: React.ReactElement[] = [];
  bars.push(
    <span
      key="guard-start"
      className={cn('h-full w-[2px] shrink-0 rounded-full', BAR_COLOR[tone])}
    />,
  );
  for (const [index, char] of [...digits].entries()) {
    const digit = Number(char);
    bars.push(
      <span
        key={`bar-${index}`}
        className={cn('h-full shrink-0 rounded-full', BAR_COLOR[tone])}
        style={{ width: `${1 + (digit % 3)}px`, marginRight: `${1 + ((digit * 7) % 3)}px` }}
      />,
    );
    if (index === Math.floor(digits.length / 2) - 1) {
      bars.push(
        <span
          key={`guard-mid-${index}`}
          className={cn('h-full w-[2px] shrink-0 rounded-full', BAR_COLOR[tone])}
        />,
      );
    }
  }
  bars.push(
    <span
      key="guard-end"
      className={cn('h-full w-[2px] shrink-0 rounded-full', BAR_COLOR[tone])}
    />,
  );

  return (
    <span
      aria-hidden="true"
      className={cn('inline-flex shrink-0 items-end overflow-hidden opacity-75', className)}
      style={{ height: `${height}px` }}
    >
      {bars}
    </span>
  );
}

export interface GtinProps {
  readonly gtin: string | null | undefined;
  readonly tone?: Tone;
  readonly empty?: string;
  readonly className?: string;
  readonly showBarcode?: boolean;
}

/** GTIN code plus its stripe fingerprint with high-contrast text. */
export function Gtin({
  gtin,
  tone = 'neutral',
  empty = 'AUCUN',
  className,
  showBarcode = true,
}: GtinProps): React.ReactElement {
  const hasValue = gtin !== null && gtin !== undefined && gtin !== '';
  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      {showBarcode && <Barcode value={gtin} tone={tone} height={16} />}
      <span className={cn('font-mono tabular text-[13px] font-semibold', TONE_TEXT[tone])}>
        {hasValue ? gtin : empty}
      </span>
    </span>
  );
}
